import React from 'react'
import { List, useTable, EditButton, DeleteButton, CreateButton } from '@refinedev/antd'
import { Table, Space } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, Pill, TableSurface, type Stat, type Tone } from '../../theme/listkit'
import { Ban, ShieldAlert } from '../../components/icons'

interface BlocklistEntry {
  term: string
  severity: 'block' | 'warn'
  created_at: string
}

const severityTone = (severity: string): Tone => {
  if (severity === 'block') return 'red'
  if (severity === 'warn') return 'gold'
  return 'neutral'
}

/**
 * Text blocklist — curate the terms the server enforces during UGC moderation.
 * 'block' terms hard-reject on the server; 'warn' terms show a client-side caution.
 */
export function TextBlocklistList() {
  // text_blocklist is small by domain (a curated term list), so a single bounded
  // page is enough to drive both the table and the count chips — no unbounded
  // fetch, no client-side aggregation over a whole table.
  const { tableProps } = useTable<BlocklistEntry>({
    resource: 'text_blocklist',
    syncWithLocation: true,
    pagination: { pageSize: 200 },
    meta: { idColumnName: 'term' },
  })

  const entries = (tableProps.dataSource ?? []) as BlocklistEntry[]
  const blocked = entries.filter((e) => e.severity === 'block').length
  const warned = entries.filter((e) => e.severity === 'warn').length

  const stats: Stat[] = [
    { label: 'Terms', value: entries.length.toLocaleString(), icon: Ban, tone: 'jaguar' },
    { label: 'Hard block', value: blocked.toLocaleString(), icon: ShieldAlert, tone: blocked > 0 ? 'red' : 'neutral' },
    { label: 'Soft warn', value: warned.toLocaleString(), tone: warned > 0 ? 'gold' : 'neutral' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Trust & Safety · Text Blocklist',
        description:
          'Terms the server evaluates against member-submitted text (posts, bios, messages). "block" = the server hard-rejects the content. "warn" = the mobile client shows an inline warning. Changes take effect immediately.',
        actions: <CreateButton />,
      }}
      stats={stats}
      statColumns={3}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="term">
            <Table.Column<BlocklistEntry>
              dataIndex="term"
              title="Term"
              render={(term: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 700, color: brand.ink, fontSize: 13.5 }}>
                  {term}
                </span>
              )}
            />
            <Table.Column<BlocklistEntry>
              dataIndex="severity"
              title="Severity"
              render={(severity: string) => (
                <Pill tone={severityTone(severity)}>{severity}</Pill>
              )}
            />
            <Table.Column<BlocklistEntry>
              dataIndex="created_at"
              title="Added"
              render={(value: string) =>
                value
                  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '—'
              }
            />
            <Table.Column<BlocklistEntry>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: BlocklistEntry) => (
                <Space>
                  <EditButton hideText size="small" recordItemId={record.term} />
                  <DeleteButton hideText size="small" recordItemId={record.term} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
